import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash, PencilSimple, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";

const blank = { name: "", email: "", phone: "", address: "" };
const STATUS_COLOR = {
  draft: "bg-amber-100 text-amber-700", ready: "bg-indigo-100 text-indigo-700", sent: "bg-blue-100 text-blue-700",
  viewed: "bg-sky-100 text-sky-700", partially_paid: "bg-teal-100 text-teal-700", paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700", void: "bg-zinc-200 text-zinc-500",
};
const PAGE = 8;

export default function Clients() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [hist, setHist] = useState(null);
  const [histClient, setHistClient] = useState(null);
  const [limit, setLimit] = useState(PAGE);

  const load = () => api.get("/customers").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (c, e) => { e?.stopPropagation(); setForm(c); setEditId(c.id); setOpen(true); };
  const openHistory = (c) => { setHistClient(c); setLimit(PAGE); setHist(null); };

  useEffect(() => {
    if (!histClient) return;
    api.get(`/customers/${histClient.id}/history`, { params: { limit } }).then((r) => setHist(r.data));
  }, [histClient, limit]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (editId) await api.put(`/customers/${editId}`, form);
    else await api.post("/customers", form);
    setOpen(false); toast.success("Client saved"); load();
  };
  const remove = async (id, e) => { e.stopPropagation(); await api.delete(`/customers/${id}`); toast.success("Client removed"); load(); };

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-3 flex items-center justify-between">
        <h1 className="font-heading text-3xl font-black tracking-tight">Clients</h1>
        <Button data-testid="add-client-btn" onClick={openNew} size="sm" className="h-10 gap-1.5"><Plus size={18} weight="bold" /> Add</Button>
      </header>

      <div className="px-4 space-y-2.5">
        {clients.length === 0 && (
          <div className="text-sm text-zinc-400 py-10 text-center border border-dashed border-zinc-200 rounded-xl" data-testid="empty-clients">
            No clients yet. They're added automatically when you invoice, or add one now.
          </div>
        )}
        {clients.map((c) => (
          <div key={c.id} data-testid={`client-${c.id}`} onClick={() => openHistory(c)}
            className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-3 active:scale-[0.99] transition-transform cursor-pointer">
            <div className="w-11 h-11 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-heading font-black shrink-0">
              {c.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-zinc-900 truncate">{c.name}</p>
              <p className="text-xs text-zinc-500 truncate">{[c.phone, c.email, c.address].filter(Boolean).join(" · ") || "No contact info"}</p>
            </div>
            <button data-testid={`client-edit-${c.id}`} onClick={(e) => openEdit(c, e)} className="text-zinc-300 hover:text-orange-500"><PencilSimple size={18} /></button>
            <button data-testid={`client-delete-${c.id}`} onClick={(e) => remove(c.id, e)} className="text-zinc-300 hover:text-red-500"><Trash size={18} /></button>
          </div>
        ))}
      </div>

      {/* Edit / New drawer */}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">{editId ? "Edit Client" : "New Client"}</DrawerTitle></DrawerHeader>
          <div className="px-4 space-y-3">
            <Input data-testid="client-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" />
            <Input data-testid="client-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
            <Input data-testid="client-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" />
            <Input data-testid="client-address" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-12" />
          </div>
          <DrawerFooter><Button data-testid="save-client-btn" onClick={save} className="h-12 font-bold">Save Client</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Payment history drawer */}
      <Drawer open={!!histClient} onOpenChange={(o) => !o && setHistClient(null)}>
        <DrawerContent className="max-w-md mx-auto max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">{histClient?.name} · Payment History</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto" data-testid="client-history">
            {hist && (
              <>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Fig label="Total Invoiced" value={money(hist.summary.total_invoiced)} testid="hist-total-invoiced" />
                  <Fig label="Total Paid" value={money(hist.summary.total_paid)} color="text-green-600" testid="hist-total-paid" />
                  <Fig label="Total Outstanding" value={money(hist.summary.total_outstanding)} color="text-orange-600" testid="hist-total-outstanding" />
                  <Fig label="Total Tips" value={money(hist.summary.total_tips)} color="text-teal-600" testid="hist-total-tips" />
                  <Fig label="Last Payment" value={hist.summary.last_payment || "—"} testid="hist-last-payment" />
                  <Fig label="Last Job" value={hist.summary.last_job || "—"} testid="hist-last-job" />
                </div>

                <div className="space-y-2">
                  {hist.invoices.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">No invoices yet.</p>}
                  {hist.invoices.map((i) => (
                    <div key={i.id} data-testid={`hist-invoice-${i.id}`} onClick={() => nav(`/invoice/${i.id}`)}
                      className="border border-zinc-200 rounded-xl p-3 cursor-pointer active:scale-[0.99] transition-transform">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-zinc-900">{i.invoice_number}</p>
                          <p className="text-[11px] text-zinc-400">Service {i.service_date || "—"} · Billed {i.invoice_date || "—"}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_COLOR[i.status] || "bg-zinc-100 text-zinc-600"}`}>{(i.status || "").replace("_", " ")}</span>
                          <CaretRight size={16} className="text-zinc-300" />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1 mt-2 text-center">
                        <MiniFig label="Total" value={money(i.total)} />
                        <MiniFig label="Discount" value={money(i.discount)} />
                        <MiniFig label="Paid" value={money(i.amount_paid)} />
                        <MiniFig label="Balance" value={money(i.balance)} accent={i.balance > 0.01} />
                      </div>
                      {i.tip_total > 0 && <p className="text-[11px] text-teal-600 mt-1">Tips received: {money(i.tip_total)}</p>}
                      {i.payments.length > 0 && (
                        <div className="mt-2 border-t border-zinc-100 pt-2 space-y-0.5">
                          {i.payments.map((p, idx) => (
                            <p key={idx} className="text-[11px] text-zinc-500 flex justify-between">
                              <span className="capitalize">{p.date} · {p.method}</span>
                              <span>{money(p.amount)}{p.tip > 0 ? ` (+${money(p.tip)} tip)` : ""}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {hist.total_count > hist.invoices.length && (
                    <Button data-testid="hist-load-more" onClick={() => setLimit((l) => l + PAGE)} variant="outline" className="w-full h-10 font-bold">Load more</Button>
                  )}
                </div>
              </>
            )}
            {!hist && <p className="text-sm text-zinc-400 py-10 text-center">Loading…</p>}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const Fig = ({ label, value, color, testid }) => (
  <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3" data-testid={testid}>
    <p className="text-[10px] tracking-wide uppercase font-bold text-zinc-400">{label}</p>
    <p className={`font-heading font-black text-base mt-0.5 truncate ${color || "text-zinc-900"}`}>{value}</p>
  </div>
);
const MiniFig = ({ label, value, accent }) => (
  <div><p className="text-[9px] uppercase font-bold text-zinc-400">{label}</p><p className={`text-xs font-semibold ${accent ? "text-orange-600" : "text-zinc-800"}`}>{value}</p></div>
);
